import { useMemo, type ReactNode } from 'react';
import {
  DEFAULT_HOTEL_RESULTS_ORDER,
  EMPTY_HOTEL_RESULTS_FILTERS,
  HotelResultsPage as IntelligenceHotelResultsPage,
  hotelOfferPagesNotice,
  hotelOfferPagesNoticeKind,
  hotelSearchAreaNotice,
  namedGuestCount,
  type HotelMapView,
  type HotelResultsCopy,
  type HotelResultsProps,
  type HotelSearchBarProps,
  type HotelSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiConfig } from '../../../shared/ui/config.js';
import { genUiI18n } from '../../../shared/ui/i18n.js';
import type { HotelPlannerBlock } from '@voll-intelligence/genui-contract';
import { filterableHotelOffers, hotelResultsFiltersCopy } from './hotel_results_filter_view.js';
import { hotelOfflineProfile, hotelOfflineRequestProp } from './hotel_offline_profile.js';
import { createHotelPlannerView, hotelDateRangeLabel } from './hotel_planner_view.js';
import {
  hotelOfferPagesNoticeCopy,
  hotelPageShareProps,
  hotelPlaceName,
  hotelResultsPageCopy,
  hotelSearchAreaNoticeCopy,
  type HotelPageShare,
} from './hotel_results_page_copy.js';
import type { ExpiredSearchRetry } from './use_expired_search_retry.js';
import type { HotelPanelList } from './use_hotel_panel_list.js';

/**
 * O que a página de resultados lê do HOST, recebido por prop e não pelo
 * Context: o mesmo vocabulário da `GenUiConfig` (locale, "buscar nesta área" e
 * o desfecho dela, o pedido offline) mais o nó onde a fila de chips do palco
 * estreito monta (#3140) — o slot do ShellBar na bancada, a segunda linha da
 * barra na tela em diálogo da conversa; `null` deixa os chips na página.
 */
export type HotelResultsPageHost = Pick<
  GenUiConfig,
  'locale' | 'hotelSearchArea' | 'hotelSearchAreaStatus' | 'offlineRequest'
> & {
  readonly chipsContainer: HTMLElement | null;
};

type HotelResultsPageProps = {
  readonly host: HotelResultsPageHost;
  readonly map: HotelMapView;
  /** A lista completa (ver `useHotelPanelList`): a mesma que o mapa em tela
   *  cheia mostra — filtros e ordem moram nela, uma fonte só. */
  readonly list: HotelPanelList;
  /** O bloco efetivo — é dele que saem lugar, datas e noites do cabeçalho. */
  readonly ui: HotelPlannerBlock;
  /** As datas correntes do formulário — o título diz a busca que produziu a
   *  página, e é o rascunho confirmado que a sabe. */
  readonly dates: { checkin?: string; checkout?: string };
  readonly resultsCopy: HotelResultsCopy;
  /** Só para as AÇÕES dos avisos (re-buscar a lista vencida) — a página não
   *  monta a barra de busca: ela vive no topo do shell do host. */
  readonly search: HotelSearchBarProps;
  readonly selectedHotelId?: string;
  readonly locked: boolean;
  /** Travamento só da NAVEGAÇÃO das linhas: navegar não é escrever. */
  readonly detailsLocked: boolean;
  readonly expiredRetry: ExpiredSearchRetry;
  readonly onMapExpand: () => void;
  readonly onOpenDetails: (hotelId: string) => void;
  /** O compartilhar da busca, ao lado do título — o host sabe montar o link. */
  readonly share?: HotelPageShare;
};

/**
 * A página determinística de resultados, com o vocabulário do kit: adapta a
 * lista completa (`useHotelPanelList`) para o `HotelResultsPage` do
 * intelligence-ui-kit. Montada pela bancada determinística (`HotelDeterministicResultsPage`)
 * e pela tela em diálogo "Ver todos os hotéis" da conversa (`hotel_results_dialog.tsx`).
 */
function HotelResultsPageOrganism({
  host,
  map,
  list,
  ui,
  dates,
  resultsCopy,
  search,
  selectedHotelId,
  locked,
  detailsLocked,
  expiredRetry,
  onMapExpand,
  onOpenDetails,
  share,
}: Readonly<HotelResultsPageProps>) {
  const { locale, t } = genUiI18n(host.locale);
  const { hotelSearchArea, hotelSearchAreaStatus, offlineRequest } = host;
  const { page, views, sources, currency } = list;
  const listNotice = hotelOfferPagesNotice({
    kind: hotelOfferPagesNoticeKind(page, expiredRetry),
    copy: hotelOfferPagesNoticeCopy(locale),
    // Fechado numa seta: `onSubmit` é método do objeto de props da faixa, e
    // passá-lo solto perderia o `this` dele no caminho.
    onSearchAgain: () => {
      search.onSubmit(search.value);
    },
    onRetry: page.retry,
  });
  const notice =
    hotelSearchAreaNotice(hotelSearchAreaStatus, hotelSearchAreaNoticeCopy(locale)) ?? listNotice;
  const filtersCopy = hotelResultsFiltersCopy(t, locale);
  const copy = hotelResultsPageCopy({ ui, dates, locale, currency });
  const offers = filterableHotelOffers(views, sources, ui.nights, ui.placeDetails, currency);
  // O perfil da busca offline: lugar, datas e ocupação com as MESMAS frases do
  // título e da barra, e os cortes ativos com os rótulos do painel.
  const profile = hotelOfflineProfile({
    place: hotelPlaceName(ui),
    dates: hotelDateRangeLabel(dates, locale),
    occupancy: search.copy.roomSummary(
      Math.max(1, search.value.rooms.length),
      namedGuestCount(search.value),
    ),
    filters: list.query.filters,
    filtersCopy,
    chipsCopy: copy.chips,
    amenityLabel: (id) =>
      views.flatMap((view) => view.amenities).find((amenity) => amenity.id === id)?.label,
  });
  return (
    <IntelligenceHotelResultsPage
      map={map}
      // Os dois hosts — a bancada (`.hbv-results`) e a tela em diálogo da conversa —
      // dão à página altura travada: a lista rola no próprio `<main>`, com a barra
      // ao lado dela e não na borda da janela (#4587).
      listScroll="own"
      chipsContainer={host.chipsContainer}
      {...(hotelSearchArea ? { onSearchArea: hotelSearchArea } : {})}
      // A coleta da LISTA, não a busca da área: aquela desarma o botão, e o
      // "Buscando…" dele é a espera da área.
      searchAreaPending={listNotice?.tone === 'loading'}
      refreshing={hotelSearchAreaStatus?.phase === 'pending'}
      offers={offers}
      currency={currency}
      resultsCopy={resultsCopy}
      filtersCopy={filtersCopy}
      copy={copy}
      {...hotelOfflineRequestProp(offlineRequest, t, profile)}
      {...(notice ? { notice } : {})}
      filters={list.query.filters}
      order={list.query.order}
      {...(selectedHotelId ? { selectedHotelId } : {})}
      locked={locked}
      {...(list.total === undefined ? {} : { total: list.total })}
      hasMore={page.hasMore}
      loadingMore={page.phase === 'loadingMore'}
      loadMoreLabel={t('hotel.results.loadMore')}
      onQueryChange={list.setQuery}
      onLoadMore={page.loadMore}
      detailsLocked={detailsLocked}
      onMapExpand={onMapExpand}
      onOpenDetails={onOpenDetails}
      {...hotelPageShareProps(share, locale)}
    />
  );
}

/** A página determinística de resultados a partir do estado da busca — a
 *  bancada (`HotelDeterministicResultsPage`) e a tela em diálogo "Ver todos os
 *  hotéis" da conversa montam esta. É aqui que o rascunho confirmado vira as
 *  DATAS do título (spreads condicionais por `exactOptionalPropertyTypes`). */
export function DeterministicResultsPage({
  host,
  view,
  list,
  effectiveUi,
  searchValue,
  search,
  locks,
  expiredRetry,
  onMapExpand,
  onOpenDetails,
  share,
}: {
  readonly host: HotelResultsPageHost;
  readonly view: ReturnType<typeof createHotelPlannerView>;
  readonly list: HotelPanelList;
  readonly effectiveUi: HotelPlannerBlock;
  readonly searchValue: HotelSearchValue;
  readonly search: HotelResultsProps['search'];
  readonly locks: { readonly panelLocked: boolean; readonly detailsLocked: boolean };
  readonly expiredRetry: ExpiredSearchRetry;
  readonly onMapExpand: () => void;
  readonly onOpenDetails: (hotelId: string) => void;
  readonly share: HotelPageShare | undefined;
}): ReactNode {
  return (
    <HotelResultsPageOrganism
      host={host}
      map={view.map}
      list={list}
      ui={effectiveUi}
      dates={{
        ...(searchValue.checkin ? { checkin: searchValue.checkin } : {}),
        ...(searchValue.checkout ? { checkout: searchValue.checkout } : {}),
      }}
      resultsCopy={view.resultsCopy}
      search={search}
      {...(effectiveUi.selectedOfferId ? { selectedHotelId: effectiveUi.selectedOfferId } : {})}
      locked={locks.panelLocked}
      detailsLocked={locks.detailsLocked}
      expiredRetry={expiredRetry}
      onMapExpand={onMapExpand}
      onOpenDetails={onOpenDetails}
      {...(share ? { share } : {})}
    />
  );
}

/** A moeda de uma página ainda sem oferta — a mesma queda da lista completa
 *  (`useHotelPanelList`); sem preço em cena ela só formata os chips de diária. */
const SEARCHING_CURRENCY = 'BRL';

const IGNORE = (): void => undefined;

/**
 * A página de resultados ENQUANTO a busca inicial da bancada corre (A6, QA prod
 * 29/09: 17–45 s de tela branca com um ícone girando). Mesma casca, mesmo título
 * e mesma cópia da página que vem depois (`HotelResultsPageOrganism`), então a
 * lista cai onde já havia forma. O status diz em que pé a busca está e nada
 * além: `searching` antes de qualquer resposta; `processing` quando o servidor
 * respondeu, no orçamento dele, que o fornecedor ainda monta a lista e não há
 * hotel em mãos. O fio não traz contagem parcial — a página não promete prazo
 * nem inventa progresso. Tudo travado: não há o que filtrar, ordenar ou abrir.
 */
export function HotelSearchingResultsPage({
  ui,
  waiting,
  share,
  locale: hostLocale,
  chipsContainer,
}: {
  readonly ui: HotelPlannerBlock;
  readonly waiting: 'searching' | 'processing';
  readonly share?: HotelPageShare;
  readonly locale: string;
  /** O slot da fila de chips do host (ver `HotelResultsPageHost`). */
  readonly chipsContainer: HTMLElement | null;
}): ReactNode {
  const { locale, t } = genUiI18n(hostLocale);
  const view = useMemo(() => createHotelPlannerView(ui, { locale }), [ui, locale]);
  const dates = {
    ...(ui.checkin ? { checkin: ui.checkin } : {}),
    ...(ui.checkout ? { checkout: ui.checkout } : {}),
  };
  return (
    <IntelligenceHotelResultsPage
      map={view.map}
      // A mesma casca da página que vem depois (`HotelResultsPageOrganism`), no
      // mesmo host travado: a lista nasce onde vai rolar.
      listScroll="own"
      chipsContainer={chipsContainer}
      offers={[]}
      currency={SEARCHING_CURRENCY}
      resultsCopy={view.resultsCopy}
      filtersCopy={hotelResultsFiltersCopy(t, locale)}
      copy={hotelResultsPageCopy({ ui, dates, locale, currency: SEARCHING_CURRENCY })}
      filters={EMPTY_HOTEL_RESULTS_FILTERS}
      order={DEFAULT_HOTEL_RESULTS_ORDER}
      searching={{
        status: t(
          waiting === 'processing' ? 'hotel.results.stillProcessing' : 'hotel.results.searching',
        ),
      }}
      onQueryChange={IGNORE}
      onMapExpand={IGNORE}
      onOpenDetails={IGNORE}
      {...hotelPageShareProps(share, locale)}
    />
  );
}
